import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../../environment/environment';

const SUPPORTED_LANGS = ['de', 'en'] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function isSupportedLang(lang: string): lang is SupportedLang {
  return (SUPPORTED_LANGS as readonly string[]).includes(lang);
}

@Component({
  selector: 'app-header',
  imports: [ButtonModule, TieredMenuModule, TranslateModule, TooltipModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  isLoggedIn = false;
  readonly currentLang = signal<SupportedLang>('en');
  readonly languageItems = signal<MenuItem[]>([]);

  private readonly router: Router = inject(Router);
  private readonly translateService = inject(TranslateService);

  private readonly languageLabels: Record<SupportedLang, string> = { de: 'Deutsch', en: 'English' };

  /** Mobile hamburger menu — mirrors the centred nav-links so users still reach
   *  every page section when the inline links collapse on small screens.
   *  Language + OSINT-console remain as dedicated icon buttons in the header. */
  readonly mobileMenuItems = computed<MenuItem[]>(() => [
    {
      label: this.translateService.instant('header.howItWorks'),
      icon: 'pi pi-cog',
      command: () => this.navigateToSection('how-it-works'),
    },
    {
      label: this.translateService.instant('header.pricing'),
      icon: 'pi pi-tag',
      command: () => this.navigateToSection('pricing'),
    },
    {
      label: this.translateService.instant('header.contact'),
      icon: 'pi pi-envelope',
      command: () => this.navigateToContact(),
    },
  ]);

  ngOnInit(): void {
    this.initLanguage();
  }

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  navigateToSection(sectionId: string): void {
    if (this.router.url === '/') {
      this.scrollToSectionElement(sectionId);
    } else {
      this.router.navigate(['/']).then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.scrollToSectionElement(sectionId);
          });
        });
      });
    }
  }

  navigateToContact(): void {
    this.router.navigate(['/contact']);
  }

  openOsint() {
    window.location.href = environment.osintURI;
  }

  setLanguage(lang: SupportedLang): void {
    this.currentLang.set(lang);
    this.translateService.use(lang);
    try {
      localStorage.setItem('smartrecon-lang', lang);
    } catch {
      // localStorage not available (e.g. private browsing); silently ignore
    }
    this.updateLanguageMenu(lang);
  }

  private updateLanguageMenu(current: SupportedLang): void {
    this.languageItems.set(
      SUPPORTED_LANGS.map((code) => ({
        label: this.languageLabels[code],
        code,
        active: code === current,
        command: () => this.setLanguage(code),
      })),
    );
  }

  private initLanguage(): void {
    let resolved: SupportedLang | null = null;

    try {
      const stored = localStorage.getItem('smartrecon-lang');
      if (stored && isSupportedLang(stored)) {
        resolved = stored;
      }
    } catch {
      // localStorage not available; proceed to next fallback
    }

    if (!resolved) {
      const browserLang = navigator.language.slice(0, 2);
      if (isSupportedLang(browserLang)) {
        resolved = browserLang;
      }
    }

    this.setLanguage(resolved ?? 'en');
  }

  private scrollToSectionElement(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (el) {
      const top = this.getElementDocumentTop(el);
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  private getElementDocumentTop(el: HTMLElement): number {
    let top = 0;
    let current: HTMLElement | null = el;
    while (current) {
      top += current.offsetTop;
      current = current.offsetParent as HTMLElement | null;
    }
    return top;
  }
}
